import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Input, Modal, SpinnerWithBackground, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'

type SpiralinksIntegration = {
	id: string
	clientId: string
	clientSecret: string
	clientName: string
	gatewayUrl: string
	targetUrl: string
	organizationId: string
	createdAt?: Date
	updatedAt?: Date
}

interface SpiralinksIntegrationModalProps {
	isOpen: boolean
	onClose: () => void
	organizationId: string
	existingIntegration?: SpiralinksIntegration
}

export function SpiralinksIntegrationModal({
	isOpen,
	onClose,
	organizationId,
	existingIntegration,
}: SpiralinksIntegrationModalProps) {
	const queryClient = useQueryClient()
	const { showToast } = useToast()
	const [isDeleting, setIsDeleting] = useState(false)
	const [formData, setFormData] = useState({
		clientId: '',
		clientSecret: '',
		clientName: '',
		gatewayUrl: '',
		targetUrl: '',
	})

	useEffect(() => {
		if (existingIntegration) {
			setFormData({
				clientId: existingIntegration.clientId || '',
				clientSecret: existingIntegration.clientSecret || '',
				clientName: existingIntegration.clientName || '',
				gatewayUrl: existingIntegration.gatewayUrl || '',
				targetUrl: existingIntegration.targetUrl || '',
			})
		} else {
			setFormData({
				clientId: '',
				clientSecret: '',
				clientName: '',
				gatewayUrl: '',
				targetUrl: '',
			})
		}
	}, [existingIntegration])

	const createMutation = useMutation({
		...trpc.admin.integrations.spiralinks.create.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.integrations.spiralinks.getByOrganizationId.queryKey({
						organizationId,
					}),
				})
				showToast('Spiralinks integration created successfully', 'info')
				onClose()
			},
			onError: (error) => {
				showToast(`Error creating integration: ${error.message}`, 'error')
			},
		}),
	})

	const updateMutation = useMutation({
		...trpc.admin.integrations.spiralinks.update.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.integrations.spiralinks.getByOrganizationId.queryKey({
						organizationId,
					}),
				})
				showToast('Spiralinks integration updated successfully', 'info')
				onClose()
			},
			onError: (error) => {
				showToast(`Error updating integration: ${error.message}`, 'error')
			},
		}),
	})

	const deleteMutation = useMutation({
		...trpc.admin.integrations.spiralinks.delete.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.integrations.spiralinks.getByOrganizationId.queryKey({
						organizationId,
					}),
				})
				showToast('Spiralinks integration deleted successfully', 'info')
				onClose()
			},
			onError: (error) => {
				showToast(`Error deleting integration: ${error.message}`, 'error')
			},
		}),
	})

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()

		if (existingIntegration) {
			updateMutation.mutate({
				id: existingIntegration.id,
				...formData,
			})
		} else {
			createMutation.mutate({
				organizationId,
				...formData,
			})
		}
	}

	const handleDelete = async () => {
		if (existingIntegration) {
			deleteMutation.mutate({ id: existingIntegration.id })
		} else {
			showToast('No integration to delete', 'error')
		}
	}

	const isLoading = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending
	const isFormValid =
		formData.clientId && formData.clientSecret && formData.clientName && formData.gatewayUrl && formData.targetUrl

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title='Configure Spiralinks Integration'
			buttons={[
				{
					children: 'Cancel',
					onClick: onClose,
					variant: 'secondary',
					disabled: isLoading,
				},
				...(existingIntegration
					? [
							{
								children: 'Delete',
								onClick: handleDelete,
								variant: 'destructive' as const,
								disabled: isLoading,
							},
						]
					: []),
				{
					children: existingIntegration ? 'Update' : 'Save',
					onClick: handleSubmit,
					variant: 'primary',
					disabled: !isFormValid || isLoading,
				},
			]}>
			<form onSubmit={handleSubmit} className='space-y-4'>
				<div>
					<Text variant='small' className='mb-4 text-neutral-600'>
						Configure your Spiralinks integration settings. All fields are required.
					</Text>
				</div>

				<Input
					label='Client Name'
					value={formData.clientName}
					onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
					placeholder='Enter client name'
					isRequired
				/>

				<Input
					label='Client ID'
					value={formData.clientId}
					onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
					placeholder='Enter client ID'
					isRequired
				/>

				<Input
					label='Client Secret'
					type='password'
					value={formData.clientSecret}
					onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })}
					placeholder='Enter client secret'
					isRequired
				/>

				<Input
					label='Gateway URL'
					value={formData.gatewayUrl}
					onChange={(e) => setFormData({ ...formData, gatewayUrl: e.target.value })}
					placeholder='https://example.com/gateway'
					hint='The URL for the Spiralinks gateway endpoint'
					isRequired
				/>

				<Input
					label='Target URL'
					value={formData.targetUrl}
					onChange={(e) => setFormData({ ...formData, targetUrl: e.target.value })}
					placeholder='https://example.com/target'
					hint='The URL where users will be redirected after authentication'
					isRequired
				/>
			</form>

			{isLoading && <SpinnerWithBackground />}
			<Modal
				isOpen={isDeleting}
				onClose={() => setIsDeleting(false)}
				title='Delete Integration'
				buttons={[
					{ children: 'Cancel', onClick: () => setIsDeleting(false), variant: 'secondary' },
					{ children: 'Delete', onClick: handleDelete, variant: 'destructive' },
				]}
			/>
		</Modal>
	)
}
